import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Users, Package, CheckCircle } from 'lucide-react';
import { formatDateTime } from '../utils/dateFormat';

import { API } from '../lib/apiBase';

export default function DashboardPage() {
  const { organization } = useAuth();
  const [stats, setStats] = useState({
    totalBeneficiaries: 0,
    totalDistributions: 0,
    todayDistributions: 0,
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);

  // Get date/time format from organization settings
  const dateFormat = organization?.date_format || 'DD/MM/YYYY';
  const timeFormat = organization?.time_format || '24h';

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/beneficiaries`);
      const beneficiaries = response.data;
      
      const today = new Date().toISOString().split('T')[0];
      let totalDist = 0;
      let todayDist = 0;
      const recent = [];

      beneficiaries.forEach(b => {
        if (b.distributions) {
          totalDist += b.distributions.length;
          b.distributions.forEach(d => {
            if (d.timestamp?.startsWith(today)) {
              todayDist++;
            }
            recent.push({
              ...d,
              beneficiary_name: `${b.first_name} ${b.surname}`
            });
          });
        }
      });

      recent.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      setStats({
        totalBeneficiaries: beneficiaries.length,
        totalDistributions: totalDist,
        todayDistributions: todayDist,
        recentActivity: recent.slice(0, 5)
      });
    } catch {
      // Silent failure: stats will show 0; toast not shown to avoid noise on dashboard load
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Beneficiaries',
      value: stats.totalBeneficiaries,
      icon: Users,
      color: 'bg-[#E9EFEA] text-[#4A5D4E]'
    },
    {
      title: 'Total Distributions',
      value: stats.totalDistributions,
      icon: Package,
      color: 'bg-[#FFF8E1] text-[#F57F17]'
    },
    {
      title: 'Distributions Today',
      value: stats.todayDistributions,
      icon: CheckCircle,
      color: 'bg-[#E8F5E9] text-[#2E7D32]'
    }
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] font-['Manrope']">Dashboard</h1>
        <p className="text-[#595959] mt-1 text-sm md:text-base">
          Welcome to {organization?.name || 'ATHINAPPY'} portal
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="border-[#E5E5E5] card-hover" data-testid={`stat-card-${stat.title.replace(/\s+/g, '-').toLowerCase()}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#8C8C8C]">{stat.title}</p>
                    <p className="text-3xl font-bold text-[#1A1A1A] mt-1 font-['Manrope']">
                      {loading ? '...' : stat.value}
                    </p>
                  </div>
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${stat.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Activity */}
      <Card className="border-[#E5E5E5]" data-testid="recent-activity-card">
        <CardHeader>
          <CardTitle className="text-xl font-['Manrope']">Recent Activity</CardTitle>
          <CardDescription>Latest distributions made</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-[#8C8C8C]">Loading...</p>
          ) : stats.recentActivity.length === 0 ? (
            <div className="text-center py-8 text-[#8C8C8C]">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No recent distributions</p>
            </div>
          ) : (
            <div className="space-y-4">
              {stats.recentActivity.map((activity) => (
                <div
                  key={activity.id || `${activity.beneficiary_name}-${activity.timestamp}`}
                  className="flex items-center justify-between p-4 rounded-lg bg-[#FAFAF8] border border-[#E5E5E5]"
                  data-testid={`activity-item-${activity.id || activity.timestamp}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-[#2E7D32]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#1A1A1A]">{activity.beneficiary_name}</p>
                      <p className="text-sm text-[#8C8C8C]">
                        {formatDateTime(activity.timestamp, dateFormat, timeFormat)}
                      </p>
                    </div>
                  </div>
                  {activity.items_delivered?.length > 0 && (
                    <span className="badge badge-success">
                      {activity.items_delivered.length} items
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-[#E5E5E5] card-hover cursor-pointer" onClick={() => window.location.href = '/beneficiaries'}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-[#E9EFEA] flex items-center justify-center">
              <Users className="w-6 h-6 text-[#4A5D4E]" />
            </div>
            <div>
              <h3 className="font-medium text-[#1A1A1A]">Manage Beneficiaries</h3>
              <p className="text-sm text-[#8C8C8C]">Add, edit or import beneficiaries</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E5E5E5] card-hover cursor-pointer" onClick={() => window.location.href = '/distribution'}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-[#FFF8E1] flex items-center justify-center">
              <Package className="w-6 h-6 text-[#F57F17]" />
            </div>
            <div>
              <h3 className="font-medium text-[#1A1A1A]">Go to Distributions</h3>
              <p className="text-sm text-[#8C8C8C]">View cards and manage check-ins</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
